import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Request from 'superagent';
import { NAMEPSACE, CAN_RATE } from "../components/Globals";
import { rangeChanged } from "../components/filter/filterOptions";

const PAGE_SIZE = 50;
// Sorting by our rating would give it away, so only raters get it as the default
const DEFAULT_ORDER_BY = CAN_RATE ? 'kino_rating' : 'imdb_rating';
const DEFAULT_ORDER_DIR = 'desc';
// While a search is active the table shows its best matches, most relevant first
const SEARCH_LIMIT = 100;
const RELEVANCE = 'relevance';

// Filter query string. For searches the release range is only sent if the user changed
// it, the same as the player's search, so both list the same films.
const filterParams = (filters, forSearch) => {
    if (!filters) return '';
    const params = new URLSearchParams();
    // released year range
    if (!forSearch || rangeChanged(filters, 'released')) {
        if (filters?.released?.min) params.set('from_year', String(filters.released.min));
        if (filters?.released?.max) params.set('to_year', String(filters.released.max));
    }
    // runtime range
    if (filters?.runtime?.min) params.set('runtime_min', String(filters.runtime.min));
    if (filters?.runtime?.max) params.set('runtime_max', String(filters.runtime.max));
    // rating thresholds
    if (filters?.imdb?.min) params.set('imdb_min', String(filters.imdb.min));
    if (filters?.imdb?.max) params.set('imdb_max', String(filters.imdb.max));
    if (filters?.rottentomatoes?.min) params.set('rotten_min', String(filters.rottentomatoes.min));
    if (filters?.rottentomatoes?.max) params.set('rotten_max', String(filters.rottentomatoes.max));
    // languages: send as repeated params for consistency with backend parsing
    if (Array.isArray(filters?.languages)) {
        const langs = filters.languages.filter(i => i && i.checked).map(i => i.value).filter(Boolean);
        langs.forEach(l => params.append('language', l));
    }
    // genres: send as repeated params (backend will also accept comma-separated)
    if (Array.isArray(filters?.genres)) {
        const gens = filters.genres.filter(i => i && i.checked).map(i => i.value).filter(Boolean);
        gens.forEach(g => params.append('genres', g));
    }
    const s = params.toString();
    return s ? `&${s}` : '';
};

// Client-side column sort for search results (all of them are loaded at once)
const sortRows = (rows, key, dir) => {
    if (key === RELEVANCE) return rows;
    const sign = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const x = a[key];
        const y = b[key];
        if (x == null && y == null) return 0;
        if (x == null) return 1;
        if (y == null) return -1;
        if (typeof x === 'number' && typeof y === 'number') return sign * (x - y);
        return sign * String(x).localeCompare(String(y));
    });
};

export function useRatedMovies(filters, searchQuery = '') {
    const [rows, setRows] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
    const [orderDir, setOrderDir] = useState(DEFAULT_ORDER_DIR);
    const searchResultsRef = useRef([]);
    const searchMode = Boolean(searchQuery);

    const serializedFilters = useMemo(() => filterParams(filters, false), [filters]);
    const searchFilters = useMemo(() => filterParams(filters, true), [filters]);

    const buildUrl = useCallback((off, ob, od) => {
        const params = new URLSearchParams();
        params.set('limit', String(PAGE_SIZE));
        params.set('offset', String(off));
        params.set('order_by', ob);
        params.set('order_dir', od);
        return `${NAMEPSACE}movies/rated?${params.toString()}${serializedFilters}`;
    }, [serializedFilters]);

    const fetchPage = useCallback(async (off, ob, od) => {
        setIsLoading(true);
        try {
            const url = buildUrl(off, ob, od);
            const resp = await Request.get(url);
            const data = JSON.parse(resp.text);
            return Array.isArray(data) ? data : [];
        } finally {
            setIsLoading(false);
        }
    }, [buildUrl]);

    const fetchSearch = useCallback(async () => {
        setIsLoading(true);
        try {
            const url = `${NAMEPSACE}movies/search?q=${encodeURIComponent(searchQuery)}&limit=${SEARCH_LIMIT}${searchFilters}`;
            const resp = await Request.get(url);
            const data = JSON.parse(resp.text);
            return Array.isArray(data.results) ? data.results : [];
        } catch (_) {
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [searchQuery, searchFilters]);

    const loadMore = useCallback(async () => {
        if (searchMode || isLoading || !hasMore) return;
        const next = offset;
        const page = await fetchPage(next, orderBy, orderDir);
        setRows(prev => [...prev, ...page]);
        setOffset(next + PAGE_SIZE);
        setHasMore(page.length === PAGE_SIZE);
    }, [searchMode, offset, isLoading, hasMore, fetchPage, orderBy, orderDir]);

    const reset = useCallback(() => {
        setRows([]);
        setOffset(0);
        setHasMore(true);
    }, []);

    const setSorting = useCallback(async (ob, od) => {
        setOrderBy(ob);
        setOrderDir(od);
        if (searchMode) {
            setRows(sortRows(searchResultsRef.current, ob, od));
            return;
        }
        // reset and fetch first page immediately for responsiveness
        setRows([]);
        setOffset(0);
        setHasMore(true);
        const page = await fetchPage(0, ob, od);
        setRows(page);
        setOffset(PAGE_SIZE);
        setHasMore(page.length === PAGE_SIZE);
    }, [searchMode, fetchPage]);

    useEffect(() => {
        // Reset and load when filters or the search change
        (async () => {
            if (searchMode) {
                const results = await fetchSearch();
                searchResultsRef.current = results;
                setOrderBy(RELEVANCE);
                setOrderDir(DEFAULT_ORDER_DIR);
                setRows(results);
                setHasMore(false);
                return;
            }
            const ob = orderBy === RELEVANCE ? DEFAULT_ORDER_BY : orderBy;
            if (ob !== orderBy) {
                setOrderBy(ob);
                setOrderDir(DEFAULT_ORDER_DIR);
            }
            const page = await fetchPage(0, ob, ob !== orderBy ? DEFAULT_ORDER_DIR : orderDir);
            setRows(page);
            setOffset(PAGE_SIZE);
            setHasMore(page.length === PAGE_SIZE);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serializedFilters, searchQuery]);

    return {
        rows,
        hasMore,
        isLoading,
        orderBy,
        orderDir,
        setSorting,
        loadMore,
        reset,
    };
}
